import { Box, MenuItem, SelectChangeEvent } from '@mui/material';
import { Formik } from 'formik';
import { OptionsObject, SnackbarKey } from 'notistack';
import { ChangeEvent, useMemo, useState } from 'react';
import { IKixieCampaignData, IParsedCampaign } from 'src/app/pages/Manage10DLC';
import { IUserAtom } from 'src/recoil/atoms/user';
import * as Yup from 'yup';

import { useRegisterNewCampaignMutation } from '../../../api/apigQuery';
import { FormikSelect } from '../../../common/formik-material-ui';
import { trackSaveAdd10DLCCampaign } from '../../../common/userTracking';
import { IAlertWithProgressBar } from '../notifications/SnackbarProviderWithStyles';
import CampaignRegistration from '../tenDLC/CampaignRegistration';

import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';

import { ModalWithStyles } from './ModalWithStyles';

interface RegisterCampaignModalProps {
  show: boolean;
  handleClose: () => void;
  campaignTemplate?: IParsedCampaign;
  brandId: string;
  userAtomContents: IUserAtom;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey | null;
  updateCampaign: any;
  successfulCampaigns: IParsedCampaign[] | undefined;
  handleTemplateUpdate: (template: IKixieCampaignData) => void;
  campaignId?: string;
  setRefetchToggleForCampaigns: () => void;
  isAdd?: boolean;
}

export default function RegisterCampaignModal(
  props: RegisterCampaignModalProps
) {
  const [registerNewCampaign] = useRegisterNewCampaignMutation();
  const {
    show,
    handleClose,
    campaignTemplate,
    brandId,
    userAtomContents,
    enqueueSnackbarWithStyles,
    updateCampaign,
    handleTemplateUpdate,
    setRefetchToggleForCampaigns,
    isAdd,
    campaignId,
  } = props;
  const [loading, setLoading] = useState(false);
  const registerCampaignSchema = useMemo(
    () =>
      Yup.object({
        campaign: Yup.object().shape({
          campaignAlias: Yup.string().required('required'),
          description: Yup.string()
            .min(40, 'Minimum 40 characters required for Campaign Description')
            .required('required'),
          usecase: Yup.string().required('required'),
          sample1: Yup.string()
            .min(20, 'Minimum 20 characters required for Sample Message #1')
            .required('required'),
          sample2: Yup.string()
            .min(20, 'Minimum 20 characters required for Sample Message #2')
            .required('required'),
          messageFlow: Yup.string()
            .min(
              40,
              'Minimum 40 characters required for SMS Opt-In Process Proof'
            )
            .max(
              320,
              'Maximum 320 characters required for SMS Opt-In Process Proof'
            )
            .required('required'),
          subUsecases: Yup.object().shape({
            F2A: Yup.bool(),
            CUSTOMER_CARE: Yup.bool(),
            HIGHER_EDUCATION: Yup.bool(),
            MARKETING: Yup.bool(),
            ACCOUNT_NOTIFICATION: Yup.bool(),
            POLLING_VOTING: Yup.bool(),
            PUBLIC_SERVICE_ANNOUNCEMENT: Yup.bool(),
            SECURITY_ALERT: Yup.bool(),
            DELIVERY_NOTIFICATION: Yup.bool(),
            FRAUD_ALERT: Yup.bool(),
          }),
        }),
      }),
    []
  );
  const checkSubUsecase = (subusecase: string, campaignTemplateData: any) => {
    return campaignTemplateData?.plivoMainCampaignJson?.subUsecases?.indexOf(
      subusecase
    ) !== undefined
      ? campaignTemplateData?.plivoMainCampaignJson?.subUsecases?.indexOf(
          subusecase
        ) !== -1
      : campaignTemplateData?.kixieCampaignFormJson?.subUsecases?.indexOf(
          subusecase
        ) !== undefined
      ? campaignTemplateData?.kixieCampaignFormJson?.subUsecases?.indexOf(
          subusecase
        ) !== -1
      : false;
  };

  const campaignTemplateData = useMemo(() => {
    if (campaignTemplate?.kixieFormJson && campaignTemplate?.plivoReturnJson) {
      return {
        ...campaignTemplate,
        kixieCampaignFormJson:
          JSON.parse(campaignTemplate?.kixieFormJson) ?? {},
        plivoMainCampaignJson:
          JSON.parse(campaignTemplate?.plivoReturnJson) ?? {},
      };
    } else if (campaignTemplate?.campaignId) {
      return {
        ...campaignTemplate,
        plivoMainCampaignJson: {
          ...campaignTemplate,
          subUsecases: campaignTemplate?.subUsecase?.split(','),
        },
        kixieCampaignFormJson: {},
      };
    }
  }, [campaignTemplate]);

  const initialValues = useMemo(
    () => ({
      campaign: {
        campaignId: campaignId ?? '',
        usecase:
          campaignTemplateData?.plivoMainCampaignJson?.usecase ??
          campaignTemplateData?.kixieCampaignFormJson?.usecase ??
          '',
        subUsecases: {
          F2A: checkSubUsecase('2FA', campaignTemplateData),
          CUSTOMER_CARE: checkSubUsecase('CUSTOMER_CARE', campaignTemplateData),
          HIGHER_EDUCATION: checkSubUsecase(
            'HIGHER_EDUCATION',
            campaignTemplateData
          ),
          MARKETING: checkSubUsecase('MARKETING', campaignTemplateData),
          ACCOUNT_NOTIFICATION: checkSubUsecase(
            'ACCOUNT_NOTIFICATION',
            campaignTemplateData
          ),
          POLLING_VOTING: checkSubUsecase(
            'POLLING_VOTING',
            campaignTemplateData
          ),
          PUBLIC_SERVICE_ANNOUNCEMENT: checkSubUsecase(
            'PUBLIC_SERVICE_ANNOUNCEMENT',
            campaignTemplateData
          ),
          SECURITY_ALERT: checkSubUsecase(
            'SECURITY_ALERT',
            campaignTemplateData
          ),
          DELIVERY_NOTIFICATION: checkSubUsecase(
            'DELIVERY_NOTIFICATION',
            campaignTemplateData
          ),
          FRAUD_ALERT: checkSubUsecase('FRAUD_ALERT', campaignTemplateData),
        },
        campaignAlias:
          campaignTemplateData?.plivoMainCampaignJson?.campaignAlias ??
          campaignTemplateData?.kixieCampaignFormJson?.campaignAlias ??
          '',
        description:
          campaignTemplateData?.plivoMainCampaignJson?.description ??
          campaignTemplateData?.kixieCampaignFormJson?.description ??
          '',
        messageFlow:
          campaignTemplateData?.plivoMainCampaignJson?.messageFlow ??
          campaignTemplateData?.kixieCampaignFormJson?.messageFlow ??
          '',
        sample1:
          campaignTemplateData?.plivoMainCampaignJson?.sample1 ??
          campaignTemplateData?.kixieCampaignFormJson?.sample1 ??
          '',
        sample2:
          campaignTemplateData?.plivoMainCampaignJson.sample2 ??
          campaignTemplateData?.kixieCampaignFormJson?.sample2 ??
          '',
        attributes: campaignTemplateData?.plivoMainCampaignJson?.attributes ??
          campaignTemplateData?.kixieCampaignFormJson?.attributes ?? {
            subscriberOptin: 1,
            subscriberOptout: 1,
            subscriberHelp: 1,
            directLending: 0,
            embeddedLink: 0,
            embeddedPhone: 0,
            affiliateMarketing: 0,
            ageGated: 0,
          },
        selectedCampaignID:
          campaignTemplateData?.plivoMainCampaignJson?.campaignId,
      },
    }),
    [campaignTemplateData, isAdd]
  );

  const { treatment } = useFeatureFlag('DASH-244-zero-bug-adding-a-campaign');
  const isDash244Enabled = useMemo(
    () => treatment === 'control' || treatment === 'on',
    [treatment]
  );

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={registerCampaignSchema}
      onSubmit={async (values, _actions) => {
        setLoading(true);
        const subs =
          values.campaign.usecase === 'MIXED' ||
          values.campaign.usecase === 'LOW_VOLUME'
            ? Object.keys(values?.campaign?.subUsecases)
                .map((keyName) => {
                  if (values?.campaign?.subUsecases?.[keyName] === true) {
                    return keyName === 'F2A' ? '2FA' : keyName;
                  }
                })
                ?.filter?.((el) => el !== undefined)
            : [];
        const dataToSend = {
          userSettings: {
            apikey: userAtomContents?.apikey,
            businessId: userAtomContents?.businessid,
            userId: userAtomContents?.userid,
            businessid: userAtomContents?.businessid,
            userid: userAtomContents?.userid,
          },
          campaign: {
            ...values?.campaign,
            brandId: brandId,
            subUsecases: subs ?? [],
            kixieId: campaignTemplate?.kixieCampaignId,
            affiliateMarketing: Boolean(
              values?.campaign?.attributes?.affiliateMarketing
            ),
            ageGated: Boolean(values?.campaign?.attributes?.ageGated),
            directLending: Boolean(values?.campaign?.attributes?.directLending),
            embeddedLink: Boolean(values?.campaign?.attributes?.embeddedLink),
            embeddedPhone: Boolean(values?.campaign?.attributes?.embeddedPhone),
          },
        };
        delete dataToSend?.campaign?.selectedCampaignID;
        try {
          const response = isAdd
            ? await registerNewCampaign(dataToSend)
            : await updateCampaign(dataToSend);
          if (
            response?.success === false ||
            response?.data?.success === false ||
            response?.data?.error
          ) {
            enqueueSnackbarWithStyles({
              message: `Failed Campaign Registration: ${
                response?.data?.error ?? ''
              }`,
              alertProps: {
                severity: 'error',
              },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `Campaign Successfully ${
                isAdd ? ' submitted' : ' Re-submitted'
              }`,
              alertProps: {
                severity: 'success',
              },
            });
          }
          setRefetchToggleForCampaigns();
        } catch (err) {
          enqueueSnackbarWithStyles({
            message: 'Failed Campaign Registration',
            alertProps: {
              severity: 'error',
            },
          });
        }
        setLoading(false);
        handleClose();
      }}
    >
      {(formik) => (
        <ModalWithStyles
          onSubmit={() => {
            formik.handleSubmit();
            trackSaveAdd10DLCCampaign();
          }}
          title={`${isAdd ? 'Add ' : 'Re-register '} Campaign`}
          aria-labelledby="example-modal-sizes-title-lg"
          show={show}
          onHide={() => {
            handleClose();
            formik.resetForm({});
          }}
          isSubmitting={formik.isSubmitting}
          isLoading={loading}
          disableSubmit={!formik.isValid}
        >
          <Box sx={{ marginTop: '16px' }}>
            {isAdd &&
              (isDash244Enabled
                ? props?.successfulCampaigns &&
                  props?.successfulCampaigns?.length > 1
                : props?.successfulCampaigns?.length &&
                  props?.successfulCampaigns?.length > 1) && (
                <FormikSelect
                  name={`campaign.selectedCampaignID`}
                  formik={formik}
                  label="Successful Campaign to Copy"
                  size="small"
                  style={{
                    minWidth: '200px',
                    marginBottom: '8px',
                  }}
                  variant="outlined"
                  onChange={(event: SelectChangeEvent | ChangeEvent<any>) => {
                    handleTemplateUpdate(event?.target?.value ?? '');
                  }}
                >
                  {props?.successfulCampaigns?.map((el, i) => (
                    <MenuItem key={el?.campaignId} value={el?.campaignId}>
                      {el?.campaignId}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
            <CampaignRegistration formik={formik} />
          </Box>
        </ModalWithStyles>
      )}
    </Formik>
  );
}
